import React, { useState, useEffect } from 'react';
import { Building2, Target, Rocket, Award, Users, TrendingUp, CheckCircle2, Sparkles, Zap, Shield } from 'lucide-react';

const About = () => {
  // Calcular anos de experiência automaticamente
  // Data de fundação: 02/06/2008
  const calculateYearsOfExperience = () => {
    const foundingDate = new Date('2008-06-02');
    const currentDate = new Date();
    const years = currentDate.getFullYear() - foundingDate.getFullYear();
    const monthDiff = currentDate.getMonth() - foundingDate.getMonth();
    
    // Se ainda não passou o mês/dia de aniversário, subtrai 1 ano
    if (monthDiff < 0 || (monthDiff === 0 && currentDate.getDate() < foundingDate.getDate())) {
      return years - 1;
    }
    return years;
  };

  const yearsOfExperience = calculateYearsOfExperience();

  // Estado para o contador de anos
  const [yearsCount, setYearsCount] = useState(0);
  const [isYearsPaused, setIsYearsPaused] = useState(false);

  // Estado para o contador de clientes
  const [clientsCount, setClientsCount] = useState(0);
  const [isClientsPaused, setIsClientsPaused] = useState(false);

  // Estado para o contador de satisfação
  const [satisfactionCount, setSatisfactionCount] = useState(0);
  const [isSatisfactionPaused, setIsSatisfactionPaused] = useState(false);

  // Estado para o contador de suporte (horas)
  const [supportHours, setSupportHours] = useState(0);
  const [isSupportPaused, setIsSupportPaused] = useState(false);

  // Efeito para animação do contador de anos (mais lento)
  useEffect(() => {
    let interval;
    
    if (isYearsPaused) {
      // Pausa de 10 segundos quando chega em 17
      const pauseTimeout = setTimeout(() => {
        setIsYearsPaused(false);
        setYearsCount(0); // Reinicia o contador
      }, 10000); // 10 segundos de pausa
      
      return () => clearTimeout(pauseTimeout);
    } else {
      // Animação de contagem de 0 até 17 (mais lenta)
      interval = setInterval(() => {
        setYearsCount((prev) => {
          if (prev >= yearsOfExperience) {
            setIsYearsPaused(true);
            return yearsOfExperience;
          }
          // Incremento de 1 em 1 para contagem mais lenta e precisa
          return prev + 1;
        });
      }, 200); // Atualiza a cada 200ms para animação mais lenta
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isYearsPaused, yearsOfExperience]);

  // Efeito para animação do contador de clientes (7.000+)
  useEffect(() => {
    let interval;
    
    if (isClientsPaused) {
      // Pausa de 10 segundos quando chega em 7000
      const pauseTimeout = setTimeout(() => {
        setIsClientsPaused(false);
        setClientsCount(0); // Reinicia o contador
      }, 10000); // 10 segundos de pausa
      
      return () => clearTimeout(pauseTimeout);
    } else {
      // Animação de contagem de 0 até 7000
      interval = setInterval(() => {
        setClientsCount((prev) => {
          if (prev >= 7000) {
            setIsClientsPaused(true);
            return 7000;
          }
          // Incremento mais rápido quanto maior o número
          const increment = prev < 100 ? 10 : prev < 500 ? 20 : prev < 1000 ? 40 : prev < 3000 ? 60 : prev < 5000 ? 80 : 100;
          return Math.min(prev + increment, 7000);
        });
      }, 30); // Atualiza a cada 30ms para animação suave
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isClientsPaused]);

  // Efeito para animação do contador de satisfação (0% até 99%)
  useEffect(() => {
    let interval;
    
    if (isSatisfactionPaused) {
      // Pausa de 10 segundos quando chega em 99%
      const pauseTimeout = setTimeout(() => {
        setIsSatisfactionPaused(false);
        setSatisfactionCount(0); // Reinicia o contador
      }, 10000);
      
      return () => clearTimeout(pauseTimeout);
    } else {
      // Animação de contagem de 0 até 99
      interval = setInterval(() => {
        setSatisfactionCount((prev) => {
          if (prev >= 99) {
            setIsSatisfactionPaused(true);
            return 99;
          }
          // Incremento de 2 em 2 para velocidade média
          return Math.min(prev + 2, 99);
        });
      }, 50); // Atualiza a cada 50ms (velocidade média)
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isSatisfactionPaused]);

  // Efeito para animação do contador de suporte (0 até 24)
  useEffect(() => {
    let interval;
    
    if (isSupportPaused) {
      // Pausa de 10 segundos quando chega em 24
      const pauseTimeout = setTimeout(() => {
        setIsSupportPaused(false);
        setSupportHours(0); // Reinicia o contador
      }, 10000);
      
      return () => clearTimeout(pauseTimeout);
    } else {
      // Animação de contagem de 0 até 24
      interval = setInterval(() => {
        setSupportHours((prev) => {
          if (prev >= 24) {
            setIsSupportPaused(true);
            return 24;
          }
          // Incremento de 1 em 1 para contagem clara
          return prev + 1;
        });
      }, 150); // Atualiza a cada 150ms (velocidade lenta-média)
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isSupportPaused]);

  const stats = [
    { number: `${yearsCount}+`, label: 'Anos de Experiência', icon: Award, isAnimated: true, isPaused: isYearsPaused, type: 'years' },
    { number: `${clientsCount.toLocaleString('pt-BR')}+`, label: 'Clientes Atendidos', icon: Users, isAnimated: true, isPaused: isClientsPaused, type: 'clients' },
    { number: `${satisfactionCount}%`, label: 'Satisfação', icon: TrendingUp, isAnimated: true, isPaused: isSatisfactionPaused, type: 'satisfaction' },
    { number: `${supportHours}/7`, label: 'Suporte Disponível', icon: Zap, isAnimated: true, isPaused: isSupportPaused, type: 'support' }
  ];

  const specialties = [
    'Suporte de TI para empresas com atendimento especializado',
    'Terceirização de TI e gestão de TI para empresas',
    'Help desk TI e suporte remoto de TI',
    'Consultoria de TI e automação comercial',
    'Implantação de sistemas fiscais e de gestão ERP'
  ];

  const differentials = [
    { icon: Zap, title: 'Suporte Técnico Ágil', desc: 'Help desk TI com resposta rápida' },
    { icon: Shield, title: 'Segurança Total', desc: 'Gestão de TI com proteção de dados' },
    { icon: Award, title: 'Qualidade Premium', desc: 'Serviços de TI certificados' },
    { icon: Users, title: 'Time Especializado', desc: 'Consultoria de TI qualificada' }
  ];

  return (
    <section id="quem-somos" className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden" style={{ scrollMarginTop: '100px' }}>
      {/* Background decorativo */}
      <div className="absolute inset-0 opacity-10 pointer-events-none overflow-hidden">
        <div className="absolute top-20 left-0 w-64 sm:w-80 md:w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-0 w-64 sm:w-72 md:w-80 h-80 bg-blue-500 rounded-full filter blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center gap-2 mb-4 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full">
            <Building2 className="w-5 h-5 text-cyan-400" />
            <span className="text-cyan-400 font-semibold text-sm uppercase tracking-wider">Sobre Nós</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 bg-gradient-to-r from-white via-cyan-200 to-blue-200 bg-clip-text text-transparent">
            Quem Somos
          </h2>
          <p className="text-xl text-blue-200 max-w-3xl mx-auto">
            Transformando tecnologia em resultados para o seu negócio
          </p>
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16 max-w-5xl mx-auto">
          {stats.map((stat, index) => (
            <div 
              key={index}
              className={`bg-gradient-to-br from-slate-800 to-slate-900 border rounded-xl p-6 text-center transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                stat.isAnimated 
                  ? 'border-cyan-400/40 hover:border-cyan-400/70 shadow-cyan-400/20 animate-pulse-subtle' 
                  : 'border-cyan-500/20 hover:border-cyan-500/50 hover:shadow-cyan-500/20'
              }`}
            >
              <stat.icon className={`w-8 h-8 mx-auto mb-3 ${stat.isAnimated ? 'text-cyan-300 animate-bounce-slow' : 'text-cyan-400'}`} />
              <div className={`text-3xl md:text-4xl font-bold mb-2 ${
                stat.isAnimated 
                  ? 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-400 animate-gradient' 
                  : 'text-white'
              }`}>
                {stat.number}
              </div>
              <div className="text-sm text-slate-400">{stat.label}</div>
              {stat.isAnimated && !stat.isPaused && (
                <div className="mt-2 text-xs text-cyan-400 opacity-70">
                  {stat.type === 'years' && '📅 Contando anos...'}
                  {stat.type === 'clients' && '⚡ Contando...'}
                  {stat.type === 'satisfaction' && '📊 Calculando...'}
                  {stat.type === 'support' && '⏰ Verificando...'}
                </div>
              )}
              {stat.isAnimated && stat.isPaused && (
                <div className="mt-2 text-xs text-green-400 opacity-70">
                  {stat.type === 'years' && '✓ Anos completos!'}
                  {stat.type === 'clients' && '✓ Meta atingida!'}
                  {stat.type === 'satisfaction' && '✓ Excelente!'}
                  {stat.type === 'support' && '✓ Sempre online!'}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Estilos para animações customizadas */}
        <style jsx>{`
          @keyframes pulse-subtle {
            0%, 100% {
              opacity: 1;
              box-shadow: 0 0 20px rgba(34, 211, 238, 0.3);
            }
            50% {
              opacity: 0.95;
              box-shadow: 0 0 30px rgba(34, 211, 238, 0.5);
            }
          }
          @keyframes bounce-slow {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-5px);
            }
          }
          @keyframes gradient {
            0% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
            100% {
              background-position: 0% 50%;
            }
          }
          .animate-pulse-subtle {
            animation: pulse-subtle 2s ease-in-out infinite;
          }
          .animate-bounce-slow {
            animation: bounce-slow 2s ease-in-out infinite;
          }
          .animate-gradient {
            background-size: 200% 200%;
            animation: gradient 3s ease infinite;
          }
        `}</style>

        {/* Conteúdo Principal */}
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8 mb-12">
          {/* Card: Quem Somos */}
          <div className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-8 shadow-xl hover:border-cyan-500/40 transition-all duration-300">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-cyan-500/10 p-3 rounded-lg">
                <Building2 className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-2xl font-bold text-white">Nossa História</h3>
            </div>
            <p className="text-slate-300 text-lg leading-relaxed mb-4">
              A <span className="text-cyan-400 font-semibold">Fernando Tech</span> é uma empresa de TI especializada em suporte técnico empresarial, terceirização de TI e consultoria de TI, com atuação em <span className="text-white font-semibold">Ribeirão Preto e região</span>.
            </p>
            <p className="text-slate-300 text-lg leading-relaxed">
              Com <span className="text-cyan-400 font-semibold">anos de experiência no mercado</span>, oferecemos serviços de TI para empresas com soluções inteligentes, seguras e personalizadas para negócios que buscam <span className="text-white font-semibold">gestão de TI com performance e confiança</span>.
            </p>
          </div>

          {/* Card: Missão */}
          <div className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-8 shadow-xl hover:border-cyan-500/40 transition-all duration-300">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-cyan-500/10 p-3 rounded-lg">
                <Target className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-2xl font-bold text-white">Nossa Missão</h3>
            </div>
            <p className="text-slate-300 text-lg leading-relaxed mb-4">
              Levar <span className="text-cyan-400 font-semibold">suporte de TI para empresas</span> de forma eficiente e confiável, com atendimento humano e acessível através de help desk TI especializado.
            </p>
            <p className="text-slate-300 text-lg leading-relaxed">
              Transformamos problemas técnicos em <span className="text-white font-semibold">soluções simples</span> — com clareza, profissionalismo e inovação em gestão de TI para empresas.
            </p>
            <div className="mt-6 flex items-center gap-2 text-cyan-400">
              <Sparkles className="w-5 h-5" />
              <span className="font-semibold">Excelência em suporte técnico empresarial</span>
            </div>
          </div>
        </div>

        {/* Especialidades */}
        <div className="max-w-6xl mx-auto mb-12">
          <div className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-8 md:p-10 shadow-xl">
            <div className="flex items-center gap-3 mb-8">
              <div className="bg-cyan-500/10 p-3 rounded-lg">
                <Rocket className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white">Nossas Especialidades</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {specialties.map((specialty, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 bg-slate-900/50 border border-slate-700 rounded-lg p-4 hover:border-cyan-500/50 transition-all duration-300 hover:scale-105"
                >
                  <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300 text-base">{specialty}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Diferenciais */}
        <div className="max-w-6xl mx-auto mb-12">
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">Nossos Diferenciais</h3>
            <p className="text-slate-400 text-lg">Por que escolher a Fernando Tech?</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {differentials.map((diff, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-slate-800 to-slate-900 border border-cyan-500/20 rounded-xl p-6 text-center hover:border-cyan-500/50 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl hover:shadow-cyan-500/20"
              >
                <div className="bg-cyan-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <diff.icon className="w-8 h-8 text-cyan-400" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2">{diff.title}</h4>
                <p className="text-slate-400 text-sm">{diff.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Compromisso Final */}
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-2xl p-8 md:p-10">
            <Award className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Nosso Compromisso com sua Empresa</h3>
            <p className="text-slate-300 text-lg leading-relaxed mb-6">
              Nosso compromisso é garantir que a <span className="text-cyan-400 font-semibold">TI terceirizada trabalhe a favor do seu negócio</span>, com suporte remoto de TI ágil, diagnóstico preciso e resultados que realmente fazem diferença no dia a dia da sua empresa.
            </p>
            <div className="inline-flex items-center gap-2 bg-cyan-500/20 border border-cyan-500/50 px-6 py-3 rounded-full">
              <CheckCircle2 className="w-5 h-5 text-cyan-400" />
              <span className="text-white font-semibold">Empresa de TI: Qualidade, Segurança e Performance</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;